import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Radio } from "lucide-react";
import PageLayout from "../../components/layout/PageLayout";
import TrendingBar from "../../components/football/TrendingBar";
import NewsCard from "../../components/football/NewsCard";
import { HERO_IMAGES } from "../../lib/constants";
import api from "../../lib/api";

export default function NFL() {
    const [sb, setSb] = useState({ events: [], week: null, season: null });
    const [standings, setStandings] = useState({ conferences: [] });
    const [news, setNews] = useState([]);

    useEffect(() => {
        api.get("/football/nfl/scoreboard").then(r => setSb(r.data)).catch(() => {});
        api.get("/football/nfl/standings").then(r => setStandings(r.data)).catch(() => {});
        api.get("/football/news", { params: { category: "NFL", limit: 12 } }).then(r => setNews(r.data)).catch(() => {});
    }, []);

    return (
        <PageLayout>
            <section className="relative pt-28 pb-12 border-b border-white/10 overflow-hidden">
                <img src={HERO_IMAGES.stadium} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-5">Digital Beast NFL</div>
                    <h1 className="font-display text-5xl md:text-7xl text-white leading-none tracking-tight" data-testid="nfl-heading">
                        NFL <span className="text-[#d4af37]">COMMAND CENTER</span>
                    </h1>
                </div>
            </section>
            <TrendingBar />

            <section className="py-10" data-testid="nfl-scoreboard-section">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="flex items-end justify-between mb-5">
                        <div>
                            <div className="section-label mb-2">Live · Powered by ESPN public data</div>
                            <h2 className="font-display text-3xl text-white">Week {sb.week} {sb.season && <span className="text-white/40">· {sb.season}</span>}</h2>
                        </div>
                        <Radio className="text-[#d4af37]" />
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {sb.events.map(e => (
                            <div key={e.id} className="surface p-4" data-testid={`nfl-event-${e.id}`}>
                                <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/40 mb-3">
                                    <span>{e.status}</span>
                                    <span>{e.broadcast?.[0] || ""}</span>
                                </div>
                                {e.teams.map(t => (
                                    <div key={t.id} className="flex items-center gap-3 py-1.5">
                                        {t.logo && <img src={t.logo} alt="" className="w-7 h-7" />}
                                        <div className="flex-1">
                                            <div className="text-white text-sm">{t.name}</div>
                                            <div className="text-white/40 text-[10px]">{t.record}</div>
                                        </div>
                                        <div className="font-display text-2xl text-white">{t.score || "—"}</div>
                                    </div>
                                ))}
                                <div className="mt-2 pt-2 border-t border-white/10 text-[10px] text-white/40">{e.venue}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-10 border-t border-white/10 bg-[#0a0a0a]" data-testid="nfl-standings-section">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-4">Standings</div>
                    <div className="grid lg:grid-cols-2 gap-6">
                        {standings.conferences.map(conf => (
                            <div key={conf.abbrev || conf.name} className="surface p-5">
                                <div className="font-display text-2xl text-white mb-4">{conf.name}</div>
                                <div className="space-y-5">
                                    {conf.divisions.map(div => (
                                        <div key={div.name}>
                                            <div className="text-[10px] text-white/40 uppercase tracking-widest mb-2">{div.name}</div>
                                            <table className="w-full text-xs">
                                                <tbody>
                                                    {div.teams.map(t => (
                                                        <tr key={t.id} className="border-t border-white/5">
                                                            <td className="py-2 flex items-center gap-2">
                                                                {t.logo && <img src={t.logo} alt="" className="w-5 h-5" />}
                                                                <span className="text-white">{t.name}</span>
                                                            </td>
                                                            <td className="py-2 text-right text-white/70 font-mono">{t.wins}-{t.losses}{t.ties && t.ties !== "0" ? `-${t.ties}` : ""}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-10" data-testid="nfl-news-section">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="flex items-end justify-between mb-5">
                        <div>
                            <div className="section-label mb-2">Headlines</div>
                            <h2 className="font-display text-3xl text-white">NFL News</h2>
                        </div>
                        <Link to="/football/news?category=NFL" className="btn-ghost text-xs">All NFL news <ChevronRight size={12} /></Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {news.map(n => <NewsCard key={n.id} item={n} testid={`nfl-news-${n.id}`} />)}
                    </div>
                </div>
            </section>
        </PageLayout>
    );
}
